import React, { useState } from "react";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    BankOutlined,
    GlobalOutlined,
    ShopOutlined,
    InfoCircleOutlined,
    CalendarOutlined,
    SafetyOutlined,
    CheckCircleOutlined,
    PercentageOutlined,
    DollarOutlined,
    StopOutlined
} from "@ant-design/icons";
import { Col, Row, Tag, message, Button } from "antd";
import CommonCard from "../../../global/card";
import { cn } from "../../../../utils/cn";
import { capitalizeFirstLetter, formatDateTime } from "../../../../utils/functions";
import SOURCES from "../../../../utils/constants";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import CommonDialog from "../../../global/dialog";
import { useQueryClient } from "@tanstack/react-query";

const EnhancedAffiliateDetails = ({
    affiliateDetail,
    // showDetail,
    setShowDetail,
    getAffiliate
}) => {
    console.log(affiliateDetail)
    const [loading, setLoading] = useState(false);
    const [toggleModalOptions, setToggleModalOptions] = useState({
        showModal: false,
        title: "",
        postMessage: "",
        actionMethod: null,
        type: ""
    });

    const queryClient = useQueryClient()
    const approveRejectMutation = useGenericMutation({
        endpoint: "affiliate/approve-reject",
        onSuccess: () => {
            message.success("Affiliate status updated successfully");
            setShowDetail(false);
            getAffiliate();
        },
        onError: (error) => {
            message.error(error?.response?.data?.message || "Failed to update status");
        }
    });

    const handleApprovedReject = async (id, status) => {
        try {
            await approveRejectMutation.mutateAsync({ id, status });
        } catch (error) {
            console.error(error);
        }
    };

    if (!affiliateDetail) return null;

    const getIconForField = (label) => {
        const iconMap = {
            Name: <UserOutlined />,
            Email: <MailOutlined />,
            Phone: <PhoneOutlined />,
            Company: <BankOutlined />,
            "Company URL": <GlobalOutlined />,
            "Business Type": <ShopOutlined />,
            Type: <InfoCircleOutlined />,
            "Created At": <CalendarOutlined />,
            "Stripe Onboarded": <SafetyOutlined />,
            "User Status": <CheckCircleOutlined />,
            "Commission Type": <PercentageOutlined />,
            "Commission Value": <DollarOutlined />,
            "Additional Information": <InfoCircleOutlined />,
        };
        return iconMap[label] || <InfoCircleOutlined />;
    };

    const getBadgeColor = (status, type) => {
        if (type === "stripe") {
            return status === "progress" ? "warning" : "success"; // Using warning for progress as per common UI, existing was danger -> danger? let's check.
            // Old code: affiliateDetail?.stripeConnectedStatus === "progress" ? "danger" : "success"
            // Wait, "progress" usually means incomplete/bad in this context? Okay, I will stick to "danger" if that's what it was, or maybe "warning".
            // Actually, in the screenshot "Stripe Onboarded" -> "Progress" is Red (Danger).
            // "User Status" -> "Pending" is Red (Danger).
        }
        if (type === "userStatus") {
            return status === SOURCES.STATUS_APPROVED ? "success" : "danger";
        }
        return "default";
    };

    const confirmAction = async () => {
        setLoading(true);
        if (toggleModalOptions.actionMethod) {
            await toggleModalOptions.actionMethod();
        }
        setLoading(false);
        setToggleModalOptions({ ...toggleModalOptions, showModal: false });
    };

    return (
        <>
            <div className="p-6" style={{ backgroundColor: "#f5f7fa" }}>
                <Row gutter={[24, 24]}>
                    {/* Basic Details */}
                    <Col span={12}>
                        <CommonCard
                            title={
                                <div className="flex items-center gap-2">
                                    <UserOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "18px" }} />
                                    <span style={{ fontSize: "16px", fontWeight: 600 }}>Basic Details</span>
                                </div>
                            }
                        >
                            <Row>
                                {[
                                    { label: "Name", value: affiliateDetail?.name },
                                    { label: "Email", value: affiliateDetail?.email },
                                    { label: "Phone", value: affiliateDetail?.phone },
                                    { label: "Company", value: affiliateDetail?.companyName },
                                    { label: "Company URL", value: affiliateDetail?.companyUrl },
                                    { label: "Business Type", value: affiliateDetail?.bussinessType },
                                    { label: "Type", value: capitalizeFirstLetter(affiliateDetail?.userType) },
                                    { label: "Created At", value: formatDateTime(affiliateDetail?.createdAt) },
                                ].map((item, idx) => (
                                    <Col span={24} key={item.label} className={cn("border-b border-gray-200 py-4", idx === 7 && "!border-b-0")}>
                                        <div className="flex justify-between items-center w-full">
                                            <span className="text-sm text-gray-600 flex items-center gap-2 font-medium">
                                                <span style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }}>
                                                    {getIconForField(item.label)}
                                                </span>
                                                {item.label}
                                            </span>
                                            <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </CommonCard>
                    </Col>

                    {/* Status & Commission */}
                    <Col span={12}>
                        <CommonCard
                            title={
                                <div className="flex items-center gap-2">
                                    <CheckCircleOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "18px" }} />
                                    <span style={{ fontSize: "16px", fontWeight: 600 }}>Status & Commission</span>
                                </div>
                            }
                        >
                            <Row>
                                {[
                                    {
                                        label: "Stripe Onboarded",
                                        value: capitalizeFirstLetter(affiliateDetail?.stripeConnectedStatus),
                                        isBadge: true,
                                        badgeColor: affiliateDetail?.stripeConnectedStatus === "progress" ? "red" : "green"
                                    },
                                    {
                                        label: "User Status",
                                        value: capitalizeFirstLetter(affiliateDetail?.userStatus),
                                        isBadge: true,
                                        badgeColor: affiliateDetail?.userStatus === SOURCES.STATUS_APPROVED ? "green" : "red"
                                    },
                                    { label: "Commission Type", value: capitalizeFirstLetter(affiliateDetail?.commissionType) },
                                    {
                                        label: "Commission Value",
                                        value: affiliateDetail?.commissionValue + (affiliateDetail?.commissionType === "percentage" ? "%" : "")
                                    },
                                ].map((item, idx) => (
                                    <Col span={24} key={item.label} className={cn("border-b border-gray-200 py-4", idx === 3 && "!border-b-0")}>
                                        <div className="flex justify-between items-center w-full">
                                            <span className="text-sm text-gray-600 flex items-center gap-2 font-medium">
                                                <span style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }}>
                                                    {getIconForField(item.label)}
                                                </span>
                                                {item.label}
                                            </span>
                                            {item.isBadge ? (
                                                <Tag color={item.badgeColor} className="m-0 px-3 py-1 font-medium">
                                                    {item.value}
                                                </Tag>
                                            ) : (
                                                <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                            )}
                                        </div>
                                    </Col>
                                ))}

                                {affiliateDetail?.userStatus === "pending" && affiliateDetail?.userStatus !== "reject" && (
                                    <div className="w-full flex gap-4 mt-6">
                                        <Button
                                            type="default"
                                            className="flex-1 !text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white h-10"
                                            icon={<CheckCircleOutlined />}
                                            onClick={() => {
                                                setToggleModalOptions({
                                                    showModal: true,
                                                    title: "Approve Affiliate/Partner",
                                                    postMessage: "Are you sure you want to approve affiliate/partner?",
                                                    actionMethod: () => handleApprovedReject(affiliateDetail?._id, SOURCES.STATUS_APPROVED)
                                                });
                                            }}
                                        >
                                            Approve
                                        </Button>
                                        <Button
                                            danger
                                            className="flex-1 border !border-red-500 !text-red-500 hover:!bg-red-500 hover:!text-white border-none hover:!border-none h-10"
                                            icon={<StopOutlined />}
                                            onClick={() => {
                                                setToggleModalOptions({
                                                    showModal: true,
                                                    title: "Reject Affiliate/Partner",
                                                    postMessage: "Are you sure you want to reject affiliate/partner?",
                                                    actionMethod: () => handleApprovedReject(affiliateDetail?._id, SOURCES.STATUS_REJECT)
                                                });
                                            }}
                                        >
                                            Reject
                                        </Button>
                                    </div>
                                )}
                            </Row>
                        </CommonCard>

                        {/* Additional Information */}
                        {affiliateDetail?.additionalInformation && (
                            <div className="mt-6">
                                <CommonCard
                                    title={
                                        <div className="flex items-center gap-2">
                                            <InfoCircleOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "18px" }} />
                                            <span style={{ fontSize: "16px", fontWeight: 600 }}>Additional Information</span>
                                        </div>
                                    }
                                >
                                    <p className="text-gray-700 whitespace-pre-wrap">{affiliateDetail?.additionalInformation}</p>
                                </CommonCard>
                            </div>
                        )}
                    </Col>
                </Row>
            </div>

            {toggleModalOptions.showModal && (
                <CommonDialog
                    title={toggleModalOptions.title}
                    description={toggleModalOptions.postMessage}
                    open={toggleModalOptions.showModal}
                    onCancel={() => setToggleModalOptions({ ...toggleModalOptions, showModal: false })}
                    onOk={confirmAction}
                    isConfirmButtonLoading={loading}
                />
            )}
        </>
    );
};

export default EnhancedAffiliateDetails;